import {
  type Event,
  Fit,
  Layout,
  Rive,
  type RiveFile,
  type StateMachineInput,
} from "@rive-app/canvas";
import { loadAndCacheRiveFile } from "./rive";

const favoriteRiveInstances: Rive[] = [];

export const cleanupFavoriteRive = () => {
  favoriteRiveInstances.forEach((riveInstance) => {
    riveInstance.cleanup();
  });
};

export const initializeFavoriteRive = () => {
  //before initializing, let us clean up all favorite instances.
  const favoriteButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(".btn.favorite");

  if (!favoriteButtons) {
    return;
  }

  const initFavoriteRiveInstances = (file: RiveFile) => {
    favoriteButtons.forEach((button: HTMLButtonElement) => {
      const heartCanvas = button.querySelector(".heart-canvas") as HTMLCanvasElement;

      const isInitalFavorited = heartCanvas.dataset.active === "true";
      const favoriteId = heartCanvas.dataset.favoriteId;
      const isEnabled = heartCanvas.dataset.enabled === "true";

      let changeStateInput: StateMachineInput | null | undefined = null;

      const rive = new Rive({
        riveFile: file,
        canvas: heartCanvas,
        stateMachines: ["HeartStateMachine"],
        autoplay: true,
        layout: new Layout({
          fit: Fit.Cover,
        }),
        onLoad: () => {
          computeSize();

          const inputs = rive.stateMachineInputs("HeartStateMachine");
          /**
           * States values
           * 1 - Outlined
           * 2 - Filled
           * 3 - Unfavorite
           * 4 - Favorite
           */
          changeStateInput = inputs?.find((i) => i.name === "ChangeState") as StateMachineInput;

          if (changeStateInput) {
            changeStateInput.value = isInitalFavorited ? 2 : 1;
          }
        },
      });

      function computeSize() {
        rive.resizeDrawingSurfaceToCanvas();
      }

      // Subscribe to window size changes and update call `resizeDrawingSurfaceToCanvas`
      window.onresize = computeSize;
      // Subscribe to devicePixelRatio changes and call `resizeDrawingSurfaceToCanvas`
      window
        .matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
        .addEventListener("change", computeSize);

      favoriteRiveInstances.push(rive);

      const handleFavoriteTimes = (addTimes: boolean) => {
        const counterElement = button.querySelector(".favorited-count") as HTMLSpanElement;
        let countValue = parseInt(counterElement.innerHTML, 10);

        if (addTimes) {
          heartCanvas.dataset.active = "true";
          countValue++;

          counterElement.classList.remove("d-none");
          counterElement.innerText = countValue.toString();
        } else {
          heartCanvas.dataset.active = "false";
          countValue--;

          if (countValue === 0) {
            counterElement.classList.add("d-none");
          }
          counterElement.innerText = countValue.toString();
        }
      };

      const favoriteAction = () => {
        handleFavoriteTimes(true);
        button.disabled = true;

        fetch(`/website-api/favorites/${favoriteId}/`, {
          credentials: "same-origin",
          method: "post",
        })
          .then((response) => response.json())
          .then(() => {
            button.disabled = false;
          });
      };

      const unFavoriteAction = () => {
        handleFavoriteTimes(false);
        button.disabled = true;

        fetch(`/website-api/user/favorite/${favoriteId}/`, {
          credentials: "same-origin",
          method: "delete",
        })
          .then((response) => response.json())
          .then(() => {
            button.disabled = false;
          });
      };

      button.addEventListener("click", () => {
        if (isEnabled && changeStateInput) {
          const isFavorited = heartCanvas.dataset.active === "true";

          if (isFavorited) {
            changeStateInput.value = 3;
            unFavoriteAction();
          } else {
            changeStateInput.value = 4;
            favoriteAction();
          }
        }
      });
    });
  };
  loadAndCacheRiveFile(
    "/inc/assets/images/rive/heart_animation.riv",
    (file: RiveFile) => {
      initFavoriteRiveInstances(file);
    },
    (event: Event) => {
      console.error("Failed to load Rive file:", event);
    },
  );
};
